import { Box, Divider, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import {
  Suspense,
  useEffect,
  useCallback,
  useMemo,
  useState,
  ReactElement,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';

import { getParentOrganizations } from '@app/adapter/organization-service';
import { ResultOrganizationCard } from '@app/components/Product/ResultOrganizationCard';
import { SearchCondition } from '@app/components/Search/SearchCondition';
import { SearchConditionSummary } from '@app/components/Search/SearchConditionSummary';
import { SearchNoResult } from '@app/components/Search/SearchNoResult';
import { SearchTypeTab } from '@app/components/Search/SearchTypeTab';
import { BottomMenu } from '@app/components/Shared/BottomMenu';
import { Breadcrumb } from '@app/components/Shared/Breadcrumb';
import {
  getProductsDescription,
  HeadBlock,
} from '@app/components/Shared/HeadBlock';
import { Loading } from '@app/components/Shared/Loading';
import { ScrollThreshold } from '@app/components/Shared/ScrollThreshold';
import {
  attributesBySmaverseSelector,
  locationsByPrefectureSelector,
  searchConditionState,
  searchResultOrganizationsState,
} from '@app/domain/catalog';
import { useDeviceType } from '@app/hooks/useBrowserHooks';
import { useGADataLayer } from '@app/hooks/useGADataLayer';
import { useSetSnackbar } from '@app/hooks/useSetSnackbar';
import { useStickyTopElement } from '@app/hooks/useStickyTopElement';
import { BOTTOM_MENU_ITEMS, GA_CUSTOM_EVENT } from '@app/static/constants';
import { theme } from '@app/theme';
import { CategoryName, CategoryType, Conditions } from '@app/types/catalog';
import { LoadableState } from '@app/types/common';
import { getSearchResultUrl } from '@app/utils/catalog';

const breadcrumbItems = [
  { label: 'トップページ', link: '/' },
  { label: '検索結果' },
];

export function Index(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const setSnackbar = useSetSnackbar();
  const [conditionState, setConditionState] =
    useRecoilState(searchConditionState);
  const smaverseAttributes = useRecoilValue(attributesBySmaverseSelector);
  const [organizations, setOrganizations] = useRecoilState(
    searchResultOrganizationsState
  );
  const [loadable, setLoadable] = useState<LoadableState>(
    LoadableState.HAS_VALUE
  );
  const prefectures = useRecoilValue(locationsByPrefectureSelector);
  const { isMobile, isDesktop } = useDeviceType();
  const { sendGAEvent } = useGADataLayer();

  const getParamToString = (value: string | null) => {
    return value ? value.split(',') : undefined;
  };

  const stickyRef = useRef<HTMLDivElement>(null);
  const { isUnStickTop, topPosition: stickTopPosition } =
    useStickyTopElement(stickyRef);
  const searchCondition = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return {
      attributeIds: getParamToString(queryParams.get('attributes')) || [],
      childrenIds: getParamToString(queryParams.get('child')) || [],
      locationIds: getParamToString(queryParams.get('locations')) || [],
      organizationId: getParamToString(queryParams.get('org')) || [],
    };
  }, [location.search]);

  const headInfo = useMemo(() => {
    const prefecture =
      searchCondition.locationIds.length === 1
        ? prefectures?.find((p) => p.id === searchCondition.locationIds[0])
            ?.name
        : undefined;
    const paramLength = Array.from(
      new URLSearchParams(location.search).keys()
    ).length;

    if (paramLength > 2 || (!prefecture && paramLength > 1)) {
      return {};
    }
    return {
      description: getProductsDescription(prefecture),
    };
  }, [location.search, prefectures, searchCondition]);

  const submitSearch = useCallback(
    (data?: Conditions) => {
      const conditions = {
        ...searchCondition,
        ...data,
        access: data?.access !== undefined ? String(data.access) : undefined,
        maxBuildingArea:
          data?.maxBuildingArea !== undefined
            ? String(data.maxBuildingArea)
            : undefined,
        maxLandArea:
          data?.maxLandArea !== undefined
            ? String(data.maxLandArea)
            : undefined,
        maxPrice:
          data?.maxPrice !== undefined ? String(data.maxPrice) : undefined,
        minBuildingArea:
          data?.minBuildingArea !== undefined
            ? String(data.minBuildingArea)
            : undefined,
        minLandArea:
          data?.minLandArea !== undefined
            ? String(data.minLandArea)
            : undefined,
        minPrice:
          data?.minPrice !== undefined ? String(data.minPrice) : undefined,
      };
      setConditionState(null);
      globalThis.scrollTo(0, 0);
      navigate(getSearchResultUrl(conditions));

      sendGAEvent(GA_CUSTOM_EVENT.SEARCH_RESULT_SEARCH, isMobile);
    },
    [isMobile, navigate, searchCondition, sendGAEvent, setConditionState]
  );

  const tabName = useMemo(() => {
    if (location.pathname.includes('/exhibitions')) {
      return CategoryName.EXHIBITION;
    } else if (location.pathname.includes('/properties')) {
      return CategoryName.PROPERTY;
    }
    return CategoryName.EXHIBITION;
  }, [location.pathname]);

  const fetchOrganizations = useCallback(
    async (nextLink?: string) => {
      if (loadable === LoadableState.LOADING) return;
      setLoadable(LoadableState.LOADING);
      try {
        const searchParams = searchCondition;

        const options = {
          ...searchParams,
          category: CategoryType.EXHIBITION,
          expand: 'mainImage',
          nextLink,
          smaverse: false,
          top: 3,
        };
        if (searchParams.organizationId.length > 0) {
          options.organizationId = searchParams.organizationId;
        }
        if (searchParams.attributeIds.length > 0) {
          options.smaverse = searchParams.attributeIds.some((attr) =>
            smaverseAttributes.some((smaverseAttr) => smaverseAttr.id === attr)
          );
        }
        const result = await getParentOrganizations(options);

        setConditionState(searchCondition);
        setOrganizations({
          data: nextLink
            ? [...organizations.data, ...result.data.value]
            : result.data.value,
          nextLink: result.data['@nextLink'],
          total: result.data.total,
        });
        setLoadable(LoadableState.HAS_VALUE);
      } catch (error) {
        setLoadable(LoadableState.HAS_ERROR);
        setSnackbar(true, '展示場の取得に失敗しました');
      }
    },
    [
      loadable,
      searchCondition,
      setConditionState,
      setSnackbar,
      setOrganizations,
      smaverseAttributes,
      organizations.data,
    ]
  );

  useEffect(() => {
    if (!conditionState) {
      setOrganizations({
        data: [],
        nextLink: '',
        scrollY: 0,
        total: 0,
      });
      void fetchOrganizations();
    } else {
      globalThis.scrollTo(0, organizations?.scrollY || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCondition]);

  const handleScrollThresholdReached = async () => {
    if (_.isEmpty(organizations.nextLink)) {
      return;
    }
    await fetchOrganizations(organizations.nextLink);
  };

  const onChangeTab = useCallback(
    (selectedTab: string) => {
      submitSearch({ ...searchCondition });
    },
    [searchCondition, submitSearch]
  );

  const handleClickProduct = useCallback(
    (id: string) => {
      setOrganizations({ ...organizations, scrollY: window.scrollY });
      navigate(`/companies/${id}`);
      sendGAEvent(GA_CUSTOM_EVENT.SEARCH_RESULT_VIEW_COMPANY, isMobile);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobile, navigate, organizations, setOrganizations]
  );

  return (
    <>
      <HeadBlock {...headInfo} />
      <Box data-e2e="searchResult-body">
        <Stack spacing={1} sx={{ bgcolor: 'secondary.main', mb: '5px' }}>
          <Breadcrumb items={breadcrumbItems} />
          <Box sx={{ position: 'relative' }}>
            {isDesktop ? (
              <Box
                ref={stickyRef}
                sx={{
                  border: 'solid 1px',
                  borderColor: theme.palette.divider,
                  float: 'left',
                  height: isUnStickTop
                    ? `calc(100vh - ${stickTopPosition}px)`
                    : '100vh',
                  position: 'sticky',
                  top: 0,
                  width: 322,
                }}
              >
                <Suspense fallback={<Loading />}>
                  <SearchCondition
                    condition={searchCondition}
                    onSearch={submitSearch}
                    showPropertySearch={false}
                  />
                </Suspense>
              </Box>
            ) : (
              <>
                <SearchTypeTab value={tabName} onChangeTab={onChangeTab} />
                <Box sx={{ width: '100%' }}>
                  <Suspense fallback={<Loading />}>
                    <SearchConditionSummary
                      condition={searchCondition}
                      onSearch={submitSearch}
                      showPropertySearch={false}
                    />
                  </Suspense>
                </Box>
              </>
            )}
            <Box
              sx={{
                border: isDesktop ? 'solid 1px' : 'none',
                borderColor: theme.palette.divider,
                borderRight: 'none',
                ...(isDesktop
                  ? {
                      marginLeft: isDesktop ? '322px' : '240px',
                      width: isDesktop
                        ? 'calc(100% - 322px)'
                        : 'calc(100% - 240px)',
                    }
                  : {}),
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  flexBasis: 0,
                  flexGrow: 1,
                  flexShrink: 1,
                  width: '100%',
                }}
              >
                {isDesktop && (
                  <SearchTypeTab value={tabName} onChangeTab={onChangeTab} />
                )}
                <Box
                  sx={{
                    marginBottom: 6,
                    marginTop: isDesktop ? 6 : 1,
                    marginX: 'auto',
                    maxWidth: '1000px',
                    width: isDesktop ? '90%' : '95%',
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                      検索結果
                    </Typography>
                    <Typography color={theme.palette.neutral.greyDark}>
                      {organizations.total}件
                    </Typography>
                  </Stack>
                  <Divider sx={{ mt: 2 }} />
                </Box>
                {loadable !== LoadableState.LOADING &&
                  organizations.data.length === 0 && <SearchNoResult />}
                <Stack
                  alignItems="center"
                  spacing={5}
                  sx={{
                    bgcolor: 'secondary.main',
                    boxSizing: 'border-box',
                    margin: '0 auto',
                    maxWidth: '1000px',
                    mb: 10,
                    width: isDesktop ? '90%' : '95%',
                  }}
                >
                  {loadable === LoadableState.LOADING && (
                    <Loading title={`検索結果(${organizations.total})`} />
                  )}
                  {organizations.data.map((organization, index) => (
                    <ResultOrganizationCard
                      key={organization.id + index}
                      organization={organization}
                      onClick={() => handleClickProduct(organization.id)}
                    />
                  ))}
                </Stack>
                <ScrollThreshold
                  disabled={_.isEmpty(organizations.nextLink)}
                  thresholdReached={handleScrollThresholdReached}
                />
              </Box>
            </Box>
          </Box>
        </Stack>

        <BottomMenu menuItems={BOTTOM_MENU_ITEMS} />
      </Box>
    </>
  );
}
